// ============================================================================
// Grupos de apps por caso de uso (CC-CONSOLE #1634 / épica #1637)
// ============================================================================
// Pedido del dueño: «reorganiza las sub apps y apps o categorías para tener
// organizadas las funcionalidades según los casos de uso de cada usuario».
//
// Un GRUPO reúne categorías completas y apps sueltas de otras categorías, sin
// mover ninguna app de su categoría real: los ids, las rutas y los derechos
// siguen donde estaban (una app puede aparecer en más de un grupo). Hay un grupo
// por caso de uso (`utils/useCases.ts`) y grupos transversales que valen para
// toda compañía.
//
// Fuente del mapeo: los presets `apps`/`menus` de
// `apps/torre-de-control-api/functions/src/utils/useCases.js` (la prueba
// `useCaseGroups.test.ts` exige que todo lo que nombra un preset esté en su
// grupo o en uno transversal), más lo que con criterio pertenece al mismo
// trabajo (p. ej. el Radar de Correo para quien monitorea).
//
// El mapeo TOLERA ausentes: una categoría o app que el tenant/rol/derechos
// ocultan (como `freight-forwarding` en CC-CONSOLE, donde es ajena) simplemente
// no se dibuja.
//
// Una compañía SIN caso de uso guardado (CC-CONSOLE, compañías viejas) nunca
// pasa por aquí: `/apps` y el menú conservan el orden de siempre.
// ============================================================================

import { USE_CASES, type UseCase } from "../../utils/useCases";

export interface LocalizedLabel {
  es: string;
  en: string;
}

export type UseCaseGroupId = UseCase | "administracion" | "otros" | "internos";

export interface UseCaseGroup {
  id: UseCaseGroupId;
  /** Caso de uso que lo tiene como grupo propio, o `transversal`. */
  useCase: UseCase | "transversal";
  title: LocalizedLabel;
  description: LocalizedLabel;
  /** Clase de Remixicon. */
  icon: string;
  /** Categorías COMPLETAS del grupo, en el orden en que se muestran. */
  categories: readonly string[];
  /** Apps sueltas de otras categorías que también pertenecen al grupo. */
  apps?: readonly string[];
  /** Recoge además toda categoría visible que ningún grupo nombra en `categories`. */
  catchAll?: true;
  /** Sólo administración de la plataforma (sus categorías ya lo exigen; es informativo). */
  platformOnly?: true;
}

/** Categoría y app de Documentación (CC-CONSOLE #1632): la app entra por su categoría. */
export const FREIGHT_FORWARDING_CATEGORY = "freight-forwarding";
export const DOCUMENTACION_APP = "documentacion";

export const USE_CASE_GROUPS: readonly UseCaseGroup[] = [
  {
    id: "transportista",
    useCase: "transportista",
    icon: "ri-truck-line",
    title: { es: "Operadores logísticos", en: "Logistics operators" },
    description: {
      es: "Flotilla monitoreada por GPS: mapa en vivo, administración, reportes, mantenimiento, patio y bolsa de carga.",
      en: "GPS-tracked fleet: live map, administration, reports, maintenance, yard and freight exchange.",
    },
    categories: ["rastreo-telemetria", "envios", "bases-y-propiedades"],
    apps: ["cargo-market-transportista", "dashboards"],
  },
  {
    id: "freight-forwarder",
    useCase: "freight-forwarder",
    icon: "ri-ship-2-line",
    title: { es: "Freight forwarders", en: "Freight forwarders" },
    description: {
      es: "Envíos multimodales con transportistas y clientes: documentación, carga aérea y marítima, embarques, CRM y reportes.",
      en: "Multimodal shipments with carriers and clients: documentation, air and sea freight, shipments, CRM and reports.",
    },
    categories: [FREIGHT_FORWARDING_CATEGORY, "cargo-market", "envios", "crm", "marketing"],
    apps: [
      "envios-link",
      "lite",
      "torre-control",
      "reportes",
      "fleet-integradores",
      "radar-correo",
      "dashboards",
      "facturacion",
    ],
  },
  {
    id: "pyme-flotilla",
    useCase: "pyme-flotilla",
    icon: "ri-store-2-line",
    title: { es: "PyMEs con reparto", en: "SMBs with delivery" },
    description: {
      es: "Repartes tus propios pedidos: punto de venta y tienda, envíos, mapa, mantenimiento y actividades del equipo.",
      en: "You deliver your own orders: point of sale and store, shipments, map, maintenance and team activities.",
    },
    categories: ["point-of-sale", "envios"],
    apps: [
      "lite",
      "admin",
      "envios-link",
      "torre-control",
      "reportes",
      "mantenimiento-vehiculos",
      "dashboards",
      "gestion-staff-actividades",
      "crm-contactos",
      "crm-negocios",
      "crm-marketing",
    ],
  },
  {
    id: "monitoreo-dedicado",
    useCase: "monitoreo-dedicado",
    icon: "ri-shield-flash-line",
    title: { es: "Monitoreo dedicado", en: "Dedicated monitoring" },
    description: {
      es: "Vigilas unidades de terceros: mapa, Centinela, alertas y geocercas, reportes y radar de correo.",
      en: "You watch third-party units: map, Centinela, alerts and geofences, reports and mail radar.",
    },
    categories: [],
    apps: ["lite", "monitoreo-ia", "admin", "reportes", "radar-correo", "inspecciones", "dashboards"],
  },
  {
    id: "administracion",
    useCase: "transversal",
    icon: "ri-settings-3-line",
    title: { es: "Administración de la cuenta", en: "Account administration" },
    description: {
      es: "Facturación, servicios, ayuda, mesa de servicio, equipo y finanzas: lo mismo para cualquier caso de uso.",
      en: "Billing, services, help, service desk, team and finance: the same for every use case.",
    },
    categories: ["administracion", "service-desk", "gestion-staff", "finanzas"],
  },
  {
    id: "otros",
    useCase: "transversal",
    icon: "ri-apps-2-line",
    title: { es: "Otras herramientas", en: "Other tools" },
    description: {
      es: "Lo demás que tu cuenta tiene habilitado.",
      en: "Everything else your account has enabled.",
    },
    categories: ["access-control", "comunity"],
    catchAll: true,
  },
  {
    id: "internos",
    useCase: "transversal",
    icon: "ri-tools-line",
    title: { es: "Sistemas internos", en: "Internal systems" },
    description: {
      es: "Herramientas de la plataforma: sólo las ve su administración.",
      en: "Platform tools: only its administration sees them.",
    },
    categories: ["internos", "b2c"],
    platformOnly: true,
  },
];

const byId = new Map<UseCaseGroupId, UseCaseGroup>(USE_CASE_GROUPS.map((g) => [g.id, g]));

/** Grupo propio de un caso de uso. */
export function groupOf(useCase: UseCase): UseCaseGroup {
  return byId.get(useCase)!;
}

/** ¿Es uno de los grupos transversales? */
export const isTransversal = (group: UseCaseGroup): boolean => group.useCase === "transversal";

/**
 * Orden de los grupos para una compañía: el suyo primero, después los demás
 * casos de uso (en el orden de la lista) y al final los transversales.
 */
export function orderedGroups(useCase: UseCase): UseCaseGroup[] {
  const own = groupOf(useCase);
  const others = USE_CASES.filter((id) => id !== useCase).map((id) => groupOf(id));
  const transversal = USE_CASE_GROUPS.filter(isTransversal);
  return [own, ...others, ...transversal];
}

/** Texto en el idioma activo (`es` salvo que el idioma empiece por `en`). */
export function groupText(label: LocalizedLabel, language: string | undefined): string {
  return (language || "").toLowerCase().startsWith("en") ? label.en : label.es;
}

/** Lo mínimo que se necesita de una categoría del menú para agrupar. */
export interface CategoryLike {
  id: string;
  apps: { id: string }[];
}

/** Categorías que algún grupo nombra completas (las demás caen en el `catchAll`). */
const CLAIMED_CATEGORIES = new Set(USE_CASE_GROUPS.flatMap((g) => g.categories));

/**
 * Lo que un grupo muestra de las categorías visibles: primero sus categorías
 * completas (en el orden del grupo), después las categorías ajenas recortadas
 * a las apps sueltas que el grupo nombra (en el orden del menú). El `catchAll`
 * suma las categorías que ningún grupo reclama. Nada se duplica dentro del
 * mismo grupo y nada se inventa: lo que no está visible no aparece.
 */
export function categoriesForGroup<T extends CategoryLike>(group: UseCaseGroup, categories: readonly T[]): T[] {
  const ownIds = new Set<string>(group.categories);
  const full = group.categories
    .map((id) => categories.find((c) => c.id === id))
    .filter((c): c is T => c !== undefined && c.apps.length > 0);

  if (group.catchAll) {
    for (const category of categories) {
      if (ownIds.has(category.id) || CLAIMED_CATEGORIES.has(category.id) || category.apps.length === 0) continue;
      ownIds.add(category.id);
      full.push(category);
    }
  }

  const loose = new Set<string>(group.apps ?? []);
  const narrowed: T[] = [];
  if (loose.size > 0) {
    for (const category of categories) {
      if (ownIds.has(category.id)) continue;
      const apps = category.apps.filter((app) => loose.has(app.id));
      if (apps.length > 0) narrowed.push({ ...category, apps });
    }
  }
  return [...full, ...narrowed];
}

export interface GroupView<T extends CategoryLike> {
  group: UseCaseGroup;
  categories: T[];
  /** Mosaicos que el grupo muestra (con los filtros de rol, derechos y JSON ya aplicados). */
  count: number;
}

const countOf = (categories: readonly CategoryLike[]): number => categories.reduce((n, c) => n + c.apps.length, 0);

/**
 * Vista por grupos de `/apps` para una compañía con caso de uso: todos los
 * grupos en su orden, sin los que quedaron vacíos.
 */
export function groupViews<T extends CategoryLike>(useCase: UseCase, categories: readonly T[]): GroupView<T>[] {
  return orderedGroups(useCase)
    .map((group) => {
      const own = categoriesForGroup(group, categories);
      return { group, categories: own, count: countOf(own) };
    })
    .filter((view) => view.count > 0);
}

/**
 * El menú lateral con caso de uso: las categorías del grupo propio primero
 * (completas y recortadas) y después las demás, en el orden de siempre y sin
 * repetir las apps que ya salieron arriba. Sin caso de uso no se llama.
 */
export function splitForSidebar<T extends CategoryLike>(useCase: UseCase, categories: readonly T[]): { own: T[]; rest: T[] } {
  const group = groupOf(useCase);
  const own = categoriesForGroup(group, categories);
  const ownIds = new Set(group.categories);
  const shown = new Set(own.flatMap((c) => c.apps.map((a) => `${c.id}/${a.id}`)));
  const rest: T[] = [];
  for (const category of categories) {
    if (ownIds.has(category.id)) continue;
    const apps = category.apps.filter((app) => !shown.has(`${category.id}/${app.id}`));
    if (apps.length > 0) rest.push(apps.length === category.apps.length ? category : { ...category, apps });
  }
  return { own, rest };
}

/** ¿El grupo nombra esta app (por su categoría completa o como app suelta)? */
export function groupContains(group: UseCaseGroup, categoryId: string, appId: string): boolean {
  return group.categories.includes(categoryId) || (group.apps ?? []).includes(appId);
}
